import React, { useState, useEffect } from 'react';
import { FiGift, FiCreditCard, FiSearch, FiCheckCircle } from 'react-icons/fi';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { useLanguage } from '../../context/LanguageContext';

const GiftCardsPage = () => {
  const { t } = useLanguage();
  const [tab, setTab] = useState('buy'); // buy | redeem | balance
  const [amount, setAmount] = useState(25);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [message, setMessage] = useState('');
  const [redeemCode, setRedeemCode] = useState('');
  const [balance, setBalance] = useState(null);
  const [myCards, setMyCards] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);

  const presets = [10, 25, 50, 100, 250];

  useEffect(() => {
    if (tab === 'balance') fetchBalance();
    if (tab === 'buy') fetchMyCards();
  }, [tab]);

  const fetchBalance = async () => {
    try {
      const { data } = await api.get('/gift-cards/credit/balance');
      setBalance(data.data.balance);
      setTransactions(data.data.transactions || []);
    } catch { /* not logged in */ }
  };

  const fetchMyCards = async () => {
    try {
      const { data } = await api.get('/gift-cards/my-cards');
      setMyCards(data.data || []);
    } catch { /* */ }
  };

  const handlePurchase = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post('/gift-cards/purchase', { amount, recipientEmail, recipientName, message });
      toast.success(`Gift card created! Code: ${data.data.code}`);
      setRecipientEmail(''); setRecipientName(''); setMessage('');
      fetchMyCards();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    setLoading(false);
  };

  const handleRedeem = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post('/gift-cards/redeem', { code: redeemCode });
      toast.success(data.message);
      setRedeemCode('');
      fetchBalance();
    } catch (err) { toast.error(err.response?.data?.message || 'Invalid code'); }
    setLoading(false);
  };

  return (
    <div className="container py-4 fd-page-container" style={{ maxWidth: '800px' }}>
      <h3 style={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: '8px' }}>
        <FiGift size={24} style={{ color: '#B91C1C' }} /> {t('giftCardsTitle')}
      </h3>

      {/* Tab buttons */}
      <div className="d-flex gap-2 my-3">
        {[['buy', t('buyGiftCard'), FiGift], ['redeem', t('redeemCode'), FiCreditCard], ['balance', t('myBalance'), FiSearch]].map(([key, label, Icon]) => (
          <button key={key} className={`btn ${tab === key ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={() => setTab(key)} style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: '5px' }}>
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {/* Buy Tab */}
      {tab === 'buy' && (
        <div className="dashboard-card p-4">
          <h5 style={{ fontWeight: 700 }}>{t('purchaseGiftCard')}</h5>
          <div className="d-flex gap-2 my-3 flex-wrap">
            {presets.map(p => (
              <button key={p} className={`btn ${amount === p ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setAmount(p)} style={{ fontWeight: 600 }}>${p}</button>
            ))}
            <input type="number" className="form-control" style={{ width: '100px' }} min={5} max={500}
              value={amount} onChange={e => setAmount(+e.target.value)} placeholder="Custom" />
          </div>
          <form onSubmit={handlePurchase}>
            <div className="row g-3 mb-3">
              <div className="col-md-6">
                <label className="form-label">{t('recipientName')}</label>
                <input className="form-control" value={recipientName} onChange={e => setRecipientName(e.target.value)} />
              </div>
              <div className="col-md-6">
                <label className="form-label">{t('recipientEmail')}</label>
                <input type="email" className="form-control" value={recipientEmail} onChange={e => setRecipientEmail(e.target.value)} />
              </div>
            </div>
            <label className="form-label">{t('personalMessage')}</label>
            <textarea className="form-control mb-3" rows={2} maxLength={500} value={message} onChange={e => setMessage(e.target.value)} />
            <button className="btn btn-primary" disabled={loading} style={{ fontWeight: 700 }}>
              {loading ? t('loading') : t('purchaseButton', { amount })}
            </button>
          </form>

          {myCards.length > 0 && (
            <div className="mt-4">
              <h6 style={{ fontWeight: 700 }}>{t('yourPurchasedCards')}</h6>
              {myCards.map(card => (
                <div key={card._id} className="d-flex justify-content-between align-items-center p-2 border-bottom" style={{ fontSize: '14px' }}>
                  <div>
                    <code style={{ fontWeight: 700, color: '#B91C1C' }}>{card.code}</code>
                    <span className="ms-2 text-muted">${card.initialAmount}</span>
                  </div>
                  <span className={`badge bg-${card.status === 'active' ? 'success' : 'secondary'}`}>{card.status}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Redeem Tab */}
      {tab === 'redeem' && (
        <div className="dashboard-card p-4">
          <h5 style={{ fontWeight: 700 }}>Redeem a Gift Card</h5>
          <p className="text-muted">Enter your gift card code to add it to your store credit.</p>
          <form onSubmit={handleRedeem} className="d-flex gap-2">
            <input className="form-control" placeholder="EBW-XXXXXXXXXXXX" value={redeemCode}
              onChange={e => setRedeemCode(e.target.value.toUpperCase())} style={{ maxWidth: '300px', fontFamily: 'monospace', fontWeight: 600 }} />
            <button className="btn btn-primary" disabled={loading || !redeemCode} style={{ fontWeight: 700 }}>
              {loading ? '...' : 'Redeem'}
            </button>
          </form>
        </div>
      )}

      {/* Balance Tab */}
      {tab === 'balance' && (
        <div className="dashboard-card p-4">
          <div className="text-center mb-4">
            <p className="text-muted mb-1">Store Credit Balance</p>
            <h2 style={{ fontWeight: 800, color: '#16A34A' }}>${(balance || 0).toFixed(2)}</h2>
          </div>
          {transactions.length > 0 && (
            <>
              <h6 style={{ fontWeight: 700 }}>Recent Transactions</h6>
              {transactions.map((t, i) => (
                <div key={i} className="d-flex justify-content-between p-2 border-bottom" style={{ fontSize: '13px' }}>
                  <div>
                    <span style={{ fontWeight: 600 }}>{t.description || t.type}</span>
                    <br /><small className="text-muted">{new Date(t.createdAt).toLocaleDateString()}</small>
                  </div>
                  <span style={{ fontWeight: 700, color: t.amount > 0 ? '#16A34A' : '#B91C1C' }}>
                    {t.amount > 0 ? '+' : ''}${t.amount?.toFixed(2)}
                  </span>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default GiftCardsPage;
